import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Avatar } from '@mui/material';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';

// --- Constants for default styling (can be adjusted) ---
const DEFAULT_WIDTH = 100;
const DEFAULT_HEIGHT = 50;
const PLACEHOLDER_BG_COLOR = 'grey.200';
const PLACEHOLDER_ICON_COLOR = 'grey.500';
// Optional: Define a project-wide fallback image URL if preferred over the icon
// const FALLBACK_IMAGE_URL = '/images/placeholder-logo.png';

export const BrandLogo = ({ src, alt, sx }) => {
  const [imgError, setImgError] = useState(false);

  const handleError = () => {
    if (!imgError) { // Prevent infinite loops if fallback itself fails
      setImgError(true);
    }
  };

  // Combine default and custom sx props
  const mergedSx = {
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
    objectFit: 'contain', // Default object fit
    ...sx, // Apply custom sx, potentially overriding defaults
  };

  // If there's an error or no source, render the placeholder
  if (imgError || !src) {
    // --- Placeholder Option 1: Icon Avatar (Default) ---
    return (
      <Avatar
        variant="rounded" // Use rounded for a generic shape
        sx={{
          bgcolor: PLACEHOLDER_BG_COLOR,
          color: PLACEHOLDER_ICON_COLOR,
          width: mergedSx.width, // Ensure placeholder matches image size
          height: mergedSx.height,
          ...sx, // Re-apply sx to Avatar itself if needed (e.g., margins)
        }}
      >
        <BrokenImageIcon />
      </Avatar>
    );

    // --- Placeholder Option 2: Fallback Image ---
    /*
    if (!FALLBACK_IMAGE_URL) {
        console.error("BrandLogo: Fallback image URL is not defined.");
        // Render icon avatar as a final fallback
        return (
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: PLACEHOLDER_BG_COLOR,
              color: PLACEHOLDER_ICON_COLOR,
              width: mergedSx.width,
              height: mergedSx.height,
              ...sx,
            }}
          >
            <BrokenImageIcon />
          </Avatar>
        );
    }
    return (
      <Box
        component="img"
        src={FALLBACK_IMAGE_URL}
        alt={alt || 'Logo placeholder'} // Provide default alt for fallback
        sx={mergedSx}
        // Note: No onError here to prevent potential loops if the fallback itself fails
      />
    );
    */
  }

  // If no error and src is provided, render the actual image
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={handleError} // Key part: sets error state on load failure
      sx={{
        display: 'block', // Prevents potential extra space below img
        ...mergedSx, // Apply merged styles
      }}
    />
  );
};

BrandLogo.propTypes = {
  /**
   * The source URL of the logo image.
   */
  src: PropTypes.string,
  /**
   * The alt text for the image (important for accessibility).
   */
  alt: PropTypes.string.isRequired,
  /**
   * Material-UI sx prop for custom styling. 
   * Allows overriding default width, height, objectFit, and adding other styles.
   */
  sx: PropTypes.object,
};

BrandLogo.defaultProps = {
  src: null,
  sx: {},
}; 